import type { ReportsFilterItem } from '../reports/reportsApi';
import type { UserAccountFilterItem } from '../userAccounts/userAccountsGateway';
import {
  buildCanonicalUserAccountQueryIdentity,
  type UserAccountQuerySortDir,
} from '../userAccounts/userAccountQuery';

const DATE_VALUE_REGEXP = /^\d{4}-\d{2}-\d{2}$/;

const normalizeDateValue = (value: unknown): string => {
  if (typeof value === 'object' && value !== null && value instanceof Date) {
    return value.toISOString().slice(0, 10);
  }

  const stringValue = String(value ?? '');
  if (DATE_VALUE_REGEXP.test(stringValue)) {
    return stringValue;
  }

  return stringValue;
};

const normalizeValue = (value: unknown): string | number | boolean | null => {
  if (value instanceof Date) {
    return normalizeDateValue(value);
  }

  if (Array.isArray(value)) {
    return JSON.stringify([...value].map((item) => normalizeValue(item)).sort());
  }

  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    value === null
  ) {
    return value;
  }

  return String(value);
};

const stableStringify = (value: unknown): string => JSON.stringify(value);

type CanonicalFilter = {
  field: string;
  op: string;
  value: string;
};

const canonicalizeFilters = (
  filters: ReadonlyArray<{ field: string; op: string; value: unknown }>
) =>
  [...filters]
    .filter((item) => item.value !== undefined && item.value !== null && item.value !== '')
    .map<CanonicalFilter>((item) => ({
      field: item.field,
      op: item.op,
      value: Array.isArray(item.value)
        ? stableStringify([...item.value].map((value) => normalizeValue(value)).sort())
        : stableStringify(normalizeValue(item.value)),
    }))
    .sort((left, right) =>
      `${left.field}:${left.op}:${left.value}`.localeCompare(
        `${right.field}:${right.op}:${right.value}`
      )
    );

const canonicalizeReportFilters = (filters: ReportsFilterItem[]) => canonicalizeFilters(filters);

export const buildReportsTableStaleReadKeys = (params: {
  filters: ReportsFilterItem[];
  page: number;
  pageSize: number;
  sort: { field: string; direction: string };
}) => {
  const canonicalFilters = canonicalizeReportFilters(params.filters);
  const sort = {
    field: params.sort.field,
    direction: params.sort.direction,
  };

  return {
    exactKey: stableStringify({
      view: 'reports-table',
      filters: canonicalFilters,
      page: params.page,
      pageSize: params.pageSize,
      sort,
    }),
    familyKey: stableStringify({
      view: 'reports-table',
      filters: canonicalFilters,
      pageSize: params.pageSize,
      sort,
    }),
  };
};

export const buildReportsMapStaleReadKeys = (params: { filters: ReportsFilterItem[] }) => {
  const canonicalFilters = canonicalizeReportFilters(params.filters);

  return {
    exactKey: stableStringify({
      view: 'reports-map',
      filters: canonicalFilters,
    }),
    familyKey: 'reports-map',
  };
};

export const buildReportDetailStaleReadKeys = (id: string) => ({
  exactKey: stableStringify({
    view: 'report-detail',
    id,
  }),
  familyKey: stableStringify({
    view: 'report-detail',
    id,
  }),
});

export const buildUserAccountsStaleReadKeys = (params: {
  filters: UserAccountFilterItem[];
  page: number;
  pageSize: number;
  sortField?: string | null;
  sortDir?: UserAccountQuerySortDir;
}) => {
  const canonicalQuery = buildCanonicalUserAccountQueryIdentity(params);

  return {
    exactKey: stableStringify({
      view: 'user-accounts',
      ...canonicalQuery,
    }),
    familyKey: stableStringify({
      view: 'user-accounts',
      filters: canonicalQuery.filters,
      pageSize: canonicalQuery.pageSize,
      sortField: canonicalQuery.sortField,
      sortDir: canonicalQuery.sortDir,
    }),
  };
};
